// ============================================================================
// useWorkItemSuppliers — list / add / update / remove supplier mappings.
// Date: April 21, 2026
// ============================================================================

'use client';

import { useState, useEffect, useCallback } from 'react';
import { authedFetch } from '@/lib/authedFetch';

const fetchWithAuth = authedFetch;

export interface SupplierSourcingMetadata {
  priority_rank?: number | null;
  selection_reason?: string | null;
  fallback_reason?: string | null;
}

export interface WorkItemSupplier {
  id: number;
  client_id: number;
  work_item_id: string;
  supplier_contact_id: number;
  supplier_name?: string;
  supplier_email?: string;
  supplier_phone?: string;
  supplier_sku: string | null;
  cost_price: number | null;
  currency_code: string;
  lead_time_days: number | null;
  min_order_qty: number | null;
  pack_size: number | null;
  is_preferred: boolean;
  is_active: boolean;
  last_purchase_date: string | null;
  last_purchase_cost: number | null;
  notes: string | null;
  priority_rank?: number | null;
  selection_reason?: string | null;
  fallback_reason?: string | null;
  product_name?: string;
  product_sku?: string;
}

export interface SupplierCreatePayload {
  work_item_id: string;
  supplier_contact_id: number;
  supplier_sku?: string | null;
  cost_price?: number | null;
  currency_code?: string;
  lead_time_days?: number | null;
  min_order_qty?: number | null;
  pack_size?: number | null;
  is_preferred?: boolean;
  notes?: string | null;
  priority_rank?: number | null;
  selection_reason?: string | null;
  fallback_reason?: string | null;
}

const BASE = '/api/v1/work-items/inventory/suppliers';

export function useWorkItemSuppliers(workItemId?: string) {
  const [suppliers, setSuppliers] = useState<WorkItemSupplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!workItemId) return;
    setLoading(true); setError(null);
    try {
      const res = await fetchWithAuth(`${BASE}?work_item_id=${encodeURIComponent(workItemId)}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed to load');
      setSuppliers(json.data || []);
    } catch (e: any) {
      setError(e.message);
    } finally { setLoading(false); }
  }, [workItemId]);

  useEffect(() => { load(); }, [load]);

  const create = useCallback(async (payload: SupplierCreatePayload) => {
    const res = await fetchWithAuth(BASE, { method: 'POST', body: JSON.stringify(payload) });
    const json = await res.json();
    if (!json.success) throw new Error(json.error || 'Failed to create');
    await load();
    return json.data;
  }, [load]);

  const update = useCallback(async (id: number, payload: Partial<WorkItemSupplier>) => {
    const res = await fetchWithAuth(`${BASE}/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
    const json = await res.json();
    if (!json.success) throw new Error(json.error || 'Failed to update');
    await load();
    return json.data;
  }, [load]);

  const remove = useCallback(async (id: number) => {
    const res = await fetchWithAuth(`${BASE}/${id}`, { method: 'DELETE' });
    const json = await res.json();
    if (!json.success) throw new Error(json.error || 'Failed to remove');
    await load();
  }, [load]);

  const setPreferred = useCallback(async (id: number) => {
    return update(id, { is_preferred: true });
  }, [update]);

  return { suppliers, loading, error, reload: load, create, update, remove, setPreferred };
}
